import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ProductionCard, type ActiveProductionDeployment, type AttachedDomain } from "@/components/overview/ProductionCard";
import { RecentDeployments, type RecentDeployment } from "@/components/overview/RecentDeployments";
import { EmptyState } from "@/components/overview/EmptyState";
import { ReleasePanel } from "@/components/ReleasePanel";

interface OverviewResponse {
  project: {
    id: string;
    slug: string;
    githubRepoUrl: string | null;
    prodBranch: string | null;
    stagingBranch: string | null;
    developmentBranch: string | null;
  };
  platformPreviewUrl: string;
  activeProductionDeployment: ActiveProductionDeployment | null;
  previousProductionDeploymentId: string | null;
  attachedDomains: AttachedDomain[];
  recentDeployments: RecentDeployment[];
}

/**
 * v4 (004-project-overview-tab) — the Overview tab. Default landing page
 * for a project.
 *
 * Single TanStack Query fetch of `/overview` (FR-025); auto-refresh every
 * 30s while the tab is in foreground (FR-026). When the active production
 * deployment is null, US5 (Phase 7) will swap in the empty state.
 * Recent deployments list lands in US4 (Phase 6).
 */
export function ProjectOverview() {
  const { org, project } = useParams<{ org: string; project: string }>();

  const overview = useQuery({
    enabled: Boolean(org && project),
    queryKey: ["project-overview", org, project],
    queryFn: () => api.get<OverviewResponse>(`/orgs/${org}/projects/${project}/overview`),
    refetchInterval: 30_000,
  });

  if (!org || !project) return null;

  if (overview.isLoading) {
    return <div className="text-sm text-muted-foreground py-10 text-center">Loading…</div>;
  }
  if (overview.isError) {
    return (
      <div className="text-sm text-danger py-10 text-center">
        Failed to load overview: {(overview.error as Error).message}
      </div>
    );
  }
  const data = overview.data!;

  return (
    <div className="space-y-4">
      <ReleasePanel />
      {data.activeProductionDeployment ? (
        <ProductionCard
          orgSlug={org}
          projectSlug={project}
          deployment={data.activeProductionDeployment}
          attachedDomains={data.attachedDomains}
          platformPreviewUrl={data.platformPreviewUrl}
          githubRepoUrl={data.project.githubRepoUrl}
          previousProductionDeploymentId={data.previousProductionDeploymentId}
        />
      ) : (
        <EmptyState orgSlug={org} projectSlug={project} prodBranch={data.project.prodBranch} />
      )}
      <RecentDeployments orgSlug={org} projectSlug={project} deployments={data.recentDeployments} />
    </div>
  );
}
